<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>{{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <image-upload-dialog
        v-if="uploadDialog"
        :title="$t('title.your_restaurant_logo')"
        :image-src="a.logoLink"
        filename-prefix="logo"
        :help-label="$t('placeholder.logo_upload')"
        :delete-confirmation-label="$t('message.confirm_delete_logo')"
        :max-width="500"
        @close="uploadDialog = false"
        @change="updateLogoLink"
      />
      <delete-account-dialog
        v-if="deleteAccountDialog"
        @close="deleteAccountDialog=false"
      />
      <validation-observer ref="observerRef">
        <v-form
          ref="form"
          lazy-validation
        >
          <settings-group
            :title="$t('settings.service_manager.appearance_title')"
            :subtitle="$t('settings.account.appearance_subtitle')"
            gutter
          >
            <v-col
              cols="12"
              md="6"
            >
              <color-picker-field
                v-model="a.color"
                class="pt-0"
                :label="$t('label.brand_color')"
              />
            </v-col>
            <v-col
              cols="12"
              :style="$vuetify.breakpoint.xsOnly ? 'display: flex; place-content: center;' : ''"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <input
                  v-model="a.logoLink"
                  type="hidden"
                >
                <image-preview-field
                  class="pl-0 pl-md-8"
                  :image-url="a.logoLink"
                  :title="$t('title.logo')"
                  :placeholder="$t('placeholder.upload_logo')"
                  :errors="errors"
                  @upload:click="uploadDialog = true"
                />
              </validation-provider>
            </v-col>
          </settings-group>

          <settings-group
            :title="$t('label.general_settings')"
            :subtitle="$t('settings.account.general_subtitle')"
            gutter
          >
            <v-col
              cols="12"
              class="d-flex flex-row"
            >
              <v-text-field
                v-model="a.id"
                class="pt-0 readonly--disabled"
                :label="$t('label.account_id')"
                readonly
              />
              <v-btn
                small
                icon
                @click="copyAccountId"
              >
                <v-icon>mdi-content-copy</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="6">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-select
                  v-model="a.timeZoneId"
                  class="pt-0"
                  :label="$t('label.time_zone')"
                  :items="timezoneItems"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-select
                  v-model="a.locale"
                  class="pt-0"
                  :label="$t('label.language')"
                  :items="locales"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
          </settings-group>
          <settings-group
            :title="$t('settings.account.languages')"
            :subtitle="$t('settings.account.languages_subtitle')"
            :gutter="$vuetify.breakpoint.lgAndUp"
          >
            <v-col
              cols="12"
              md="6"
            >
              <v-select
                v-model="defaultLanguage"
                :items="enabledLanguages"
                :label="$t('label.default_language')"
                :disabled="enabledLanguages.length === 1"
              />
            </v-col>
            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <p :class="['subheading mb-2']">
                {{ $t('label.enabled_languages') }}
              </p>
              <v-simple-table dense>
                <template #default>
                  <thead>
                    <tr>
                      <th class="text-left w-100">
                        {{ $t('label.language') }}
                      </th>
                      <th class="text-left">
                        {{ $t('label.enabled') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="l in locales.filter((lang) => showAllLanguages || enabledLanguages.includes(lang) )"
                      :key="l.locale"
                    >
                      <td>{{ l.language }}</td>
                      <td class="pb-2">
                        <v-checkbox
                          :input-value="enabledLanguages.includes(l)"
                          :disabled="(enabledLanguages.length === 1 && enabledLanguages.includes(l)) || [a.locale, defaultLanguage].includes(l.locale)"
                          dense
                          hide-details
                          @change="$event ? enabledLanguages.push(l) : enabledLanguages = enabledLanguages.filter((lang) => lang.locale !== l.locale)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div class="text-center">
                <v-btn
                  v-if="!showAllLanguages"
                  outlined
                  x-small
                  @click="showAllLanguages = true"
                >
                  {{ $t('button.add_languages') }}
                </v-btn>
              </div>
            </v-col>
          </settings-group>
          <settings-group
            :title="$t('label.address')"
            :subtitle="$t('settings.account.address_subtitle')"
            gutter
          >
            <v-col cols="12">
              <validated-text-field
                v-model="a.accountName"
                class="pt-0"
                rules="required|min:2|max:255"
                :label="$t('label.restaurant_name')"
                required
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.streetAddress"
                class="pt-0"
                :label="$t('label.street')"
                rules="required|min:2|max:255"
                required
              />
            </v-col>
            <v-col cols="8">
              <validated-text-field
                v-model="a.city"
                class="pt-0"
                :label="$t('label.city')"
                rules="required|min:2|max:255"
                required
              />
            </v-col>
            <v-col cols="4">
              <validated-text-field
                v-model="a.postalCode"
                class="pt-0"
                :label="$t('label.zipcode')"
                rules="required|min:2|max:50"
                required
              />
            </v-col>
            <v-col cols="8">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-select
                  v-model="a.countryCode"
                  class="pt-0"
                  :label="$t('label.country')"
                  :error-messages="errors"
                  :items="countries"
                />
              </validation-provider>
            </v-col>
            <v-col cols="4">
              <validated-text-field
                v-model="a.countryState"
                class="pt-0"
                :label="$t('label.state')"
                rules="required|min:2|max:50"
              />
            </v-col>
          </settings-group>

          <settings-group
            :title="$t('label.contact_details')"
            :subtitle="$t('settings.account.contact_details_subtitle')"
            gutter
          >
            <v-col cols="6">
              <validated-text-field
                v-model="a.phone"
                class="pt-0"
                :label="$t('label.phone_number')"
                :rules="`max:50|validPhone:${defaultCC}`"
              />
            </v-col>
            <v-col cols="6">
              <validated-text-field
                v-model="a.email"
                class="pt-0"
                :label="$t('label.email')"
                rules="required|max:254|validEmail"
                required
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.website"
                class="pt-0"
                :label="$t('label.website')"
                prefix="https://"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.facebook"
                class="pt-0"
                label="Facebook"
                prefix="https://www.facebook.com/"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.instagram"
                class="pt-0"
                label="Instagram"
                prefix="https://www.instagram.com/"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.tiktok"
                class="pt-0"
                label="TikTok"
                prefix="https://www.tiktok.com/"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.tripadvisor"
                class="pt-0"
                label="Tripadvisor"
                :prefix="tripadvisorDomains"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.x"
                class="pt-0"
                label="X"
                prefix="https://x.com/"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.yelp"
                class="pt-0"
                label="Yelp"
                prefix="https://www.yelp.com/"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
            <v-col cols="12">
              <validated-text-field
                v-model="a.youtube"
                class="pt-0"
                label="Youtube"
                prefix="https://www.youtube.com/"
                hide-prefix-on-blur
                strip-prefix-from-value
                rules="max:255"
              />
            </v-col>
          </settings-group>
          <v-row class="center d-flex flex-column">
            <v-btn
              text
              x-small
              color="red"
              @click="deleteAccountDialog=true"
            >
              {{ $t('button.permanently_delete_account') }}
            </v-btn>
          </v-row>
        </v-form>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
// Removed i18n:
// label.regional_settings

import Vue, { computed, ref } from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import ImageUploadDialog from '@/components/dialogs/ImageUploadDialog.vue';
import Account from '@/model/Account';
import Locale from '@/model/Locale';
import TimeZone from '@/model/TimeZone';
import Country from '@/model/Country';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performSaveAction } from '@/services/vue-utils';
import DeleteAccountDialog from '@/components/views/settings/DeleteAccountDialog.vue';
import httpClient from '@/api/http-client';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import ImagePreviewField from '@/components/forms/ImagePreviewField.vue';
import copy from 'copy-to-clipboard';
import useStoreWatch from '@/composables/store-watch';
import store from '@/store/store';
import { ValidationObserver } from 'vee-validate';
import i18n from '@/plugins/i18n';
import useUnsavedGuard from '@/composables/unsaved-guard';
import { useToast } from '../helpers/vue2-helpers';

interface TimezoneMenuItem {
  text?: string; value?: number; header?:string; divider?:boolean;
}

const toast = useToast();

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const a = ref(new Account());
const olda = ref(new Account());

const uploadDialog = ref(false);
const deleteAccountDialog = ref(false);

const enabledLanguages = ref([] as Locale[]);
const showAllLanguages = ref(false);
const defaultLanguage = ref('en');

const timezones = ref([] as TimeZone[]);
const locales = ref([] as Locale[]);
const countries = ref([] as Country[]);

const isLangChanged = computed(() => {
  const defaultLocale = store.getters.defaultLocale as string;
  const enabledLocalesSet = store.getters.enabledLocalesSet as Set<string>;
  return defaultLanguage.value !== defaultLocale
    || enabledLanguages.value.length !== enabledLocalesSet.size
    || enabledLanguages.value.some((l) => !enabledLocalesSet.has(l.locale));
});

const isChanged = computed(() => !isModelEqualToModel(a.value, olda.value) || isLangChanged.value);
useUnsavedGuard(isChanged);

const timezoneItems = computed(() => {
  const tzItems = [] as TimezoneMenuItem[];
  let prevKey = '';
  timezones.value.forEach((tz) => {
    const key = tz.name.split('/')[0].trim();
    if (key !== prevKey) {
      if (tzItems.length > 0) { tzItems.push({ divider: true }); }
      tzItems.push({ header: key });
      prevKey = key;
    }
    tzItems.push({ text: tz.name.replace(`${key} / `, '').trim(), value: tz.id });
  });
  return tzItems;
});

const autofillCountry = async () => {
  try {
    const countryCode = await httpClient.getCountryCode();
    if (!countryCode || a.value.countryCode) return; // no country code detected or no need to update it any more
    Vue.set(a.value, 'countryCode', countryCode); // autofill country code
  } catch (e) {
    console.log('Autofill country code error: ', e);
  }
};

const reset = async () => {
  const acctSettings = store.state.settings.account;
  a.value = cloneModel(acctSettings);
  olda.value = acctSettings;

  locales.value = store.getters.sortedLocales;

  countries.value = store.getters.sortedCountries;
  timezones.value = store.getters.sortedTimezones;

  defaultLanguage.value = store.getters.defaultLocale;
  const enabledLocalesSet = store.getters.enabledLocalesSet as Set<string>;
  enabledLanguages.value = locales.value.filter((l) => enabledLocalesSet.has(l.locale));

  if (!a.value.countryCode) autofillCountry();
};
useStoreWatch(reset);

const save = async () => {
  const entity = cloneModel(a.value);
  entity.defaultLocale = defaultLanguage.value;
  entity.enabledLocales = enabledLanguages.value.map((l) => l.locale);

  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendAccount', { entity }),
  );
  if (ok) {
    reset();
  }
};

const updateLogoLink = async (image: File | null) => {
  const ok = await performSaveAction(
    undefined,
    async () => {
      let imageUrl = '';

      // save image and get url, if undefined, image is being removed
      if (image !== null) {
        const key = store.state.settings.account.serviceManagerAccessKey ?? '';
        imageUrl = await httpClient.uploadImageFile(image, key);
      }

      // save account
      olda.value.logoLink = imageUrl;
      a.value.logoLink = imageUrl;
      await store.dispatch('sendAccount', { entity: olda.value });
    },
  );

  if (ok) {
    uploadDialog.value = false;
  }
};

const copyAccountId = () => {
  copy(`${a.value.id}`);
  toast.success(i18n.t('message.account_id_copied'));
};

const defaultCC = computed(() => a.value.countryCode);

const tripadvisorDomains = [
  'https://www.tripadvisor.com/',
  'https://www.tripadvisor.co.uk/',
  'https://www.tripadvisor.ca/',
  'https://www.tripadvisor.com.au/',
  'https://www.tripadvisor.in/',
  'https://www.tripadvisor.de/',
  'https://www.tripadvisor.fr/',
  'https://www.tripadvisor.es/',
  'https://www.tripadvisor.it/',
  'https://www.tripadvisor.nl/',
  'https://www.tripadvisor.jp/',
  'https://www.tripadvisor.com.br/',
  'https://www.tripadvisor.com.mx/',
  'https://www.tripadvisor.se/',
  'https://www.tripadvisor.dk/',
  'https://www.tripadvisor.no/',
  'https://www.tripadvisor.fi/',
  'https://www.tripadvisor.ie/',
  'https://www.tripadvisor.sg/',
  'https://www.tripadvisor.co.nz/',
  'https://www.tripadvisor.co.za/',
  'https://www.tripadvisor.com.tr/',
  'https://www.tripadvisor.com.ar/',
  'https://www.tripadvisor.com.pe/',
  'https://www.tripadvisor.com.ve/',
  'https://www.tripadvisor.com.co/',
  'https://www.tripadvisor.com.ec/',
  'https://www.tripadvisor.com.ph/',
  'https://www.tripadvisor.com.my/',
  'https://www.tripadvisor.com.hk/',
  'https://www.tripadvisor.com.tw/',
  'https://www.tripadvisor.co.kr/',
  'https://www.tripadvisor.com.vn/',
  'https://www.tripadvisor.com.id/',
  'https://www.tripadvisor.com.th/',
  'https://www.tripadvisor.com.pk/',
  'https://www.tripadvisor.com.sa/',
  'https://www.tripadvisor.com.eg/',
  'https://www.tripadvisor.com.il/',
  'https://www.tripadvisor.com.ua/',
  'https://www.tripadvisor.pl/',
  'https://www.tripadvisor.cz/',
  'https://www.tripadvisor.hu/',
  'https://www.tripadvisor.ro/',
  'https://www.tripadvisor.bg/',
  'https://www.tripadvisor.hr/',
  'https://www.tripadvisor.si/',
  'https://www.tripadvisor.sk/',
  'https://www.tripadvisor.lt/',
  'https://www.tripadvisor.lv/',
  'https://www.tripadvisor.ee/',
  'https://www.tripadvisor.is/',
  'https://www.tripadvisor.pt/',
];

</script>

<style lang="scss">

  .v-select-list .v-subheader {
    position: sticky;
    top: 0px;

    background: white;
    border-bottom: 1px #E0E0E0 solid;

    &.theme--dark {
      background: #1E1E2A;
      border-bottom: 1px #393944 solid;
    }
  }

  .v-select-list .v-subheader {
    z-index: 2;
  }

</style>
